import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Box,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography
} from '@mui/material';
import StatsCard from '../components/StatsCard';
import PeriodStats from '../models/PeriodStats';
import { getFunnelList, getFunnels, getOverviewCharts, getStats } from '../Api';
import FunnelDto from '../models/dtos/FunnelDto';
import FunnelsCard from '../components/FunnelsCard';
import UserPurposeCard from '../components/UserPurposeCard';
import FullOverviewDto from '../models/dtos/FullOverviewDto';
import OverviewChartsDto from '../models/dtos/OverviewChartsDto';
import { useNavigate } from 'react-router-dom';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

type StatsOsTypeFilter = 'all' | 'android' | 'ios';

export default function HomePage() {
    const [stats, setStats] = React.useState<PeriodStats[]>([]);
    const [funnels, setFunnels] = React.useState<FunnelDto[]>([]);
    const [loadingStats, setLoadingStats] = React.useState<boolean>(true);
    const [selectedOsType, setSelectedOsType] = React.useState<StatsOsTypeFilter>('all');
    const navigate = useNavigate();

    const mapStats = (data: FullOverviewDto, charts: OverviewChartsDto) => {
        const result: PeriodStats[] = [];

        result.push({ name: "Revenue", today: formatRevenue(data.Today.revenue), week: formatRevenue(data.Week.revenue), month: formatRevenue(data.Month.revenue), yesterday: formatRevenue(data.Yesterday.revenue), chart: charts.revenue });
        result.push({ name: "Bets", today: data.Today.betsPlaced.toLocaleString(), week: data.Week.betsPlaced.toLocaleString(), month: data.Month.betsPlaced.toLocaleString(), yesterday: data.Yesterday.betsPlaced.toLocaleString(), chart: charts.bets });
        result.push({ name: "Purchases", today: data.Today.purchases.toLocaleString(), week: data.Week.purchases.toLocaleString(), month: data.Month.purchases.toLocaleString(), yesterday: data.Yesterday.purchases.toLocaleString(), chart: charts.purchases });
        result.push({ name: "New users", today: data.Today.usersRegistered.toLocaleString(), week: data.Week.usersRegistered.toLocaleString(), month: data.Month.usersRegistered.toLocaleString(), yesterday: data.Yesterday.usersRegistered.toLocaleString(), chart: charts.users });
        return result;
    };

    const renderOsTypeOption = (value: StatsOsTypeFilter) => {
        if (value === 'all') {
            return <Typography variant="body2">All platforms</Typography>;
        }

        const label = value === 'android' ? 'Android' : 'iOS';
        const icon = value === 'android' ? '/android_logo.png' : '/ios_logo.png';

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <img src={icon} alt={label} width={20} height={20} />
                <Typography variant="body2">{label}</Typography>
            </Box>
        );
    };

    React.useEffect(() => {
        const fetchData = async () => {
            const [fetchedFunnels, listOfFunnels] = await Promise.all([getFunnels(), getFunnelList()]);
            fetchedFunnels.forEach(x => {
                const funnelConfig = listOfFunnels.find(f => f.id === x.name);
                if (funnelConfig !== undefined) {
                    x.name = funnelConfig.name;
                }
            });
            setFunnels(fetchedFunnels);
        };

        void fetchData();
    }, []);

    React.useEffect(() => {
        let isActive = true;

        const fetchStatsData = async () => {
            setLoadingStats(true);
            setStats([]);
            const osType = selectedOsType === 'all' ? undefined : selectedOsType;
            const [overview, charts] = await Promise.all([getStats(osType), getOverviewCharts(osType)]);

            if (!isActive) {
                return;
            }

            setStats(mapStats(overview, charts));
            setLoadingStats(false);
        };

        void fetchStatsData();

        return () => {
            isActive = false;
        };
    }, [selectedOsType]);

    return (
        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}
            >
                <Typography variant="h3">Statistics</Typography>
                <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel id="statistics-os-type-label">Platform</InputLabel>
                    <Select
                        labelId="statistics-os-type-label"
                        label="Platform"
                        value={selectedOsType}
                        onChange={(event) => setSelectedOsType(event.target.value as StatsOsTypeFilter)}
                        renderValue={(value) => renderOsTypeOption(value as StatsOsTypeFilter)}
                    >
                        <MenuItem value="all">{renderOsTypeOption('all')}</MenuItem>
                        <MenuItem value="android">{renderOsTypeOption('android')}</MenuItem>
                        <MenuItem value="ios">{renderOsTypeOption('ios')}</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
            {loadingStats ? (
                <CircularProgress size={64} style={{ marginBottom: '32px' }} />
            ) : (
                <Grid container spacing={5} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }}>{
                    stats.map(x =>
                        <Grid item xs={1} key={x.name}>
                            <StatsCard name={x.name} today={x.today} yesterday={x.yesterday} week={x.week} month={x.month} chartDates={x.chart.map(c => new Date(c.date))} chartValues={x.chart.map(c => c.value)} />
                        </Grid>)
                }
                </Grid>
            )}
            <UserPurposeCard osType={selectedOsType === 'all' ? undefined : selectedOsType} />
            <Typography variant="h3">Funnels | Offers</Typography>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 5 }} justifyContent="flex-end">
                <Grid item xs={1} key="editConfiguration" alignContent="center">
                    <Button variant="outlined" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/funnels')}>Funnels</Button>
                </Grid>
                <Grid item xs={1} key="viewOffers" alignContent="center">
                    <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/funnels/offers')}>View offers</Button>
                </Grid>
            </Grid>
            {<Grid container spacing={5} columns={{ xs: 1, sm: 2, md: 2, lg: 3 }}>{
                funnels.map(x =>
                    <Grid item xs={1} key={x.name}>
                        <FunnelsCard {...x} />
                    </Grid>)
            }
            </Grid>}
        </Stack >
    );
}
